import {
  FETCH_PARTNERS,
  FETCH_PARTNERS_SUCCESS,
  FETCH_PARTNERS_FAILED,
  EDIT_PARTNER,
  APPROVE_PARTNER,
  SET_CURRENT_PARTNER,
  FETCH_MY_PARTNER,
  FETCH_MY_PARTNER_SUCCESS,
  FETCH_MY_PARTNER_FAILED,
  STOP_FETCH_MY_PARTNER,
  UPDATE_MY_PARTNER,
  FETCH_PARTNER_INCOMING,
  FETCH_PARTNER_INCOMING_SUCCESS,
  STOP_FETCH_PARTNER_INCOMING,
  DISMISS_PARTNER_INCOMING,
  RESPOND_PARTNER_ORDER,
} from '../store/types';
import store from '../store/store';
import { firebase } from '../config/configureFirebase';
import { onValue, set, remove, push, update, get, off, child } from 'firebase/database';
import { uploadBytesResumable, getDownloadURL } from 'firebase/storage';

const snapshotToArray = (snap) => {
  const val = snap.val();
  if (!val) return [];
  return Object.keys(val).map((id) => ({ ...val[id], id }));
};

export const fetchPartners = () => (dispatch) => {
  const { partnersRef } = firebase;
  dispatch({ type: FETCH_PARTNERS, payload: null });
  onValue(partnersRef, (snap) => {
    if (snap.val()) {
      dispatch({
        type: FETCH_PARTNERS_SUCCESS,
        payload: snapshotToArray(snap),
      });
    } else {
      dispatch({
        type: FETCH_PARTNERS_FAILED,
        payload:
          (store.getState().languagedata?.defaultLanguage &&
            store.getState().languagedata.defaultLanguage.no_data) ||
          'No partners',
      });
    }
  });
};

export const fetchPartnersByOwner = (uid) => (dispatch) => {
  const { partnersByOwnerRef } = firebase;
  dispatch({ type: FETCH_PARTNERS, payload: null });
  onValue(partnersByOwnerRef(uid), (snap) => {
    if (snap.val()) {
      dispatch({
        type: FETCH_PARTNERS_SUCCESS,
        payload: snapshotToArray(snap),
      });
    } else {
      dispatch({ type: FETCH_PARTNERS_SUCCESS, payload: [] });
    }
  });
};

export const fetchPartnersByType = (type) => (dispatch) => {
  const { partnersByTypeRef } = firebase;
  dispatch({ type: FETCH_PARTNERS, payload: null });
  onValue(partnersByTypeRef(type), (snap) => {
    if (snap.val()) {
      dispatch({
        type: FETCH_PARTNERS_SUCCESS,
        payload: snapshotToArray(snap),
      });
    } else {
      dispatch({ type: FETCH_PARTNERS_SUCCESS, payload: [] });
    }
  });
};

export const fetchPartnerById = (partnerId) => async (dispatch) => {
  const { partnerEditRef } = firebase;
  const snap = await get(partnerEditRef(partnerId));
  if (snap.val()) {
    const partner = { ...snap.val(), id: partnerId };
    dispatch({ type: SET_CURRENT_PARTNER, payload: partner });
    return partner;
  }
  return null;
};

export const setCurrentPartner = (partner) => (dispatch) => {
  dispatch({ type: SET_CURRENT_PARTNER, payload: partner });
};

const uploadCover = async (partnerId, image) => {
  const { partnerCoverRef } = firebase;
  await uploadBytesResumable(partnerCoverRef(partnerId), image);
  return getDownloadURL(partnerCoverRef(partnerId));
};

const uploadGalleryImage = async (partnerId, n, image) => {
  const { partnerImageRef } = firebase;
  await uploadBytesResumable(partnerImageRef(partnerId, n), image);
  return getDownloadURL(partnerImageRef(partnerId, n));
};

export const editPartner = (partner, method) => async (dispatch) => {
  const { partnersRef, partnerEditRef } = firebase;
  dispatch({ type: EDIT_PARTNER, payload: { method, partner } });

  if (method === 'Add') {
    const ref = await push(partnersRef, {
      ...partner,
      approved: !!partner.approved,
      active: partner.active !== false,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    });
    return ref.key;
  }
  if (method === 'Delete') {
    await remove(partnerEditRef(partner.id));
    return partner.id;
  }
  if (method === 'UpdateCover') {
    const url = await uploadCover(partner.id, partner.coverFile);
    await update(partnerEditRef(partner.id), {
      'images/cover': url,
      updatedAt: Date.now(),
    });
    return url;
  }
  if (method === 'AddGalleryImage') {
    const url = await uploadGalleryImage(partner.id, partner.imageIndex, partner.imageFile);
    await update(partnerEditRef(partner.id), {
      [`images/gallery/${partner.imageIndex}`]: url,
      updatedAt: Date.now(),
    });
    return url;
  }
  // default Update
  await set(partnerEditRef(partner.id), {
    ...partner,
    updatedAt: Date.now(),
  });
  return partner.id;
};

export const approvePartner = (partnerId, approved, byUid) => async (dispatch) => {
  const { partnerEditRef } = firebase;
  dispatch({ type: APPROVE_PARTNER, payload: { partnerId, approved } });
  await update(partnerEditRef(partnerId), {
    approved: !!approved,
    approvedBy: byUid || null,
    approvedAt: Date.now(),
    updatedAt: Date.now(),
  });
};

/**
 * Patch parcial sobre /partners/{id} sin requerir que myPartner esté
 * cargado en redux (no toca otros campos). Útil en el flujo de registro
 * justo después de crear el partner.
 */
export const patchPartner = (partnerId, patch) => async () => {
  if (!partnerId || !patch) return;
  const { partnerEditRef } = firebase;
  await update(partnerEditRef(partnerId), { ...patch, updatedAt: Date.now() });
};

/**
 * Sube un documento del socio a Storage (`partnerDocs/{partnerId}/{docId}`)
 * y devuelve la URL pública. Usado en el onboarding antes de tener un
 * partnerId real: en ese caso pasa el uid del owner como `partnerId`
 * temporal y la cloud function lo migra cuando se cree el partner.
 */
export const uploadPartnerDoc = (partnerId, docId, file) => async () => {
  if (!partnerId || !docId || !file) throw new Error('uploadPartnerDoc: faltan args.');
  const { partnerDocsRef } = firebase;
  const r = partnerDocsRef(partnerId, docId);
  await uploadBytesResumable(r, file);
  return getDownloadURL(r);
};

/**
 * Lee de un tirón las reseñas de un socio (no real-time — para la pantalla
 * PartnerReviews y resúmenes en BrowsePartners).
 */
export const fetchPartnerReviews = (partnerId) => async () => {
  if (!partnerId) return [];
  const { partnerReviewsRef } = firebase;
  const snap = await get(partnerReviewsRef(partnerId));
  const val = snap.val() || {};
  return Object.keys(val).map((id) => ({ ...val[id], id }))
    .sort((a, b) => (b.dated || b.createdAt || 0) - (a.dated || a.createdAt || 0));
};

/**
 * Respuesta del socio a una reseña: guarda response.{text,respondedAt,by}.
 */
export const respondPartnerReview = (partnerId, reviewId, text) => async () => {
  if (!partnerId || !reviewId) return;
  const { partnerReviewsRef } = firebase;
  await update(child(partnerReviewsRef(partnerId), reviewId), {
    response: { text: String(text || '').trim(), respondedAt: Date.now(), by: 'partner' },
  });
};

export const addPartnerReview = (partnerId, review) => async () => {
  const { partnerReviewsRef, partnerEditRef } = firebase;
  const ref = push(partnerReviewsRef(partnerId), {
    ...review,
    dated: Date.now(),
  });
  // simple rolling avg update
  const partnerSnap = await get(partnerEditRef(partnerId));
  if (partnerSnap.val()) {
    const partner = partnerSnap.val();
    const prevAvg = partner.rating?.avg || 0;
    const prevCount = partner.rating?.count || 0;
    const nextCount = prevCount + 1;
    const nextAvg = (prevAvg * prevCount + Number(review.rating || 0)) / nextCount;
    await update(partnerEditRef(partnerId), {
      'rating/avg': Math.round(nextAvg * 10) / 10,
      'rating/count': nextCount,
      updatedAt: Date.now(),
    });
  }
  return ref.key;
};

// ──────────────────────────────────────────────────────────
// MyPartner — el negocio del socio logueado en la app de socio.
// ──────────────────────────────────────────────────────────

/**
 * Subscribe en tiempo real al negocio del usuario logueado.
 * Resuelve `partners.orderByChild('owner').equalTo(uid)` y guarda el primer
 * resultado en `state.partnerdata.myPartner`.
 */
export const fetchMyPartner = (uid) => (dispatch) => {
  if (!uid) {
    dispatch({ type: FETCH_MY_PARTNER_SUCCESS, payload: null });
    return;
  }
  const { partnersByOwnerRef } = firebase;
  dispatch({ type: FETCH_MY_PARTNER, payload: uid });
  const ref = partnersByOwnerRef(uid);
  onValue(ref, (snap) => {
    const val = snap.val();
    if (!val) {
      dispatch({ type: FETCH_MY_PARTNER_SUCCESS, payload: null });
      return;
    }
    // Tomamos el primero (1 dueño = 1 negocio, según decisión del plan).
    const firstId = Object.keys(val)[0];
    if (!firstId) {
      dispatch({ type: FETCH_MY_PARTNER_SUCCESS, payload: null });
      return;
    }
    const partner = { ...val[firstId], id: firstId };
    dispatch({ type: FETCH_MY_PARTNER_SUCCESS, payload: partner });
  }, (err) => {
    dispatch({ type: FETCH_MY_PARTNER_FAILED, payload: err?.message || 'error' });
  });
};

export const stopFetchMyPartner = (uid) => (dispatch) => {
  if (!uid) return;
  const { partnersByOwnerRef } = firebase;
  off(partnersByOwnerRef(uid));
  dispatch({ type: STOP_FETCH_MY_PARTNER, payload: uid });
};

/**
 * Actualiza campos del partner del usuario logueado.
 * Hace update parcial sobre `partners/{id}` y refresca local optimistically.
 */
export const updateMyPartner = (patch) => async (dispatch) => {
  const state = store.getState().partnerdata;
  const me = state?.myPartner;
  if (!me?.id) {
    throw new Error('updateMyPartner: no hay partner cargado en redux.');
  }
  const { partnerEditRef } = firebase;
  // Optimistic local merge para que la UI responda inmediato.
  dispatch({ type: UPDATE_MY_PARTNER, payload: { ...patch, updatedAt: Date.now() } });
  try {
    await update(partnerEditRef(me.id), { ...patch, updatedAt: Date.now() });
  } catch (err) {
    // Si falla, el suscriptor de fetchMyPartner traerá el valor real del server.
    console.warn('updateMyPartner failed', err?.message || err);
    throw err;
  }
};

/**
 * Toggle rápido de "abierto/cerrado" del negocio.
 */
export const setAcceptingOrders = (value) => async (dispatch) => {
  return dispatch(updateMyPartner({ acceptingOrders: !!value }));
};

/**
 * Suscripción real-time a pedidos PENDING dirigidos a este socio.
 * Alimenta el modal `PartnerIncomingOrder`. Llamar al loguearse / cuando
 * `myPartner.id` está disponible. `stopFetchPartnerIncoming(partnerId)`
 * para desuscribirse en logout.
 */
export const fetchPartnerIncoming = (partnerId) => (dispatch) => {
  if (!partnerId) return;
  const { partnerOrdersByPartnerRef } = firebase;
  const ref = partnerOrdersByPartnerRef(partnerId);
  dispatch({ type: FETCH_PARTNER_INCOMING, payload: partnerId });
  onValue(ref, (snap) => {
    const raw = snap.val() || {};
    const list = Object.keys(raw)
      .map((id) => ({ ...raw[id], id }))
      // Solo PENDING (los demás se ven en PartnerOrders).
      .filter((o) => o.status === 'PENDING')
      // Auto-servicio NO dispara el modal (lo maneja el conductor); el socio
      // solo lo ve en la lista informativa.
      .filter((o) => !o.selfService)
      // Hotel: STAY también dispara modal pero con timer largo (lo decide la UI).
      .sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
    dispatch({ type: FETCH_PARTNER_INCOMING_SUCCESS, payload: list });
  });
};

export const stopFetchPartnerIncoming = (partnerId) => (dispatch) => {
  if (!partnerId) return;
  const { partnerOrdersByPartnerRef } = firebase;
  try { off(partnerOrdersByPartnerRef(partnerId)); } catch (_) { /* noop */ }
  dispatch({ type: STOP_FETCH_PARTNER_INCOMING });
};

/**
 * Marca un order como "ya tratado" en el cliente para que el modal pase al
 * siguiente sin esperar el round-trip de DB. El subscribe lo retira cuando
 * la DB confirme el nuevo status.
 */
export const dismissPartnerIncoming = (orderId) => (dispatch) => {
  if (!orderId) return;
  dispatch({ type: DISMISS_PARTNER_INCOMING, payload: orderId });
};

/**
 * Aceptar un pedido entrante: pasa a CONFIRMED y registra acceptedAt.
 */
export const acceptPartnerOrder = (orderId) => async (dispatch) => {
  if (!orderId) return;
  const { partnerOrderEditRef } = firebase;
  dispatch({ type: RESPOND_PARTNER_ORDER, payload: { orderId, action: 'accept' } });
  dispatch(dismissPartnerIncoming(orderId));
  await update(partnerOrderEditRef(orderId), {
    status: 'CONFIRMED',
    acceptedAt: Date.now(),
    updatedAt: Date.now(),
  });
};

/**
 * Rechazar un pedido entrante. `reason` opcional. Si `auto` true marca el
 * order como timeout (vencido el contador 30s).
 */
export const rejectPartnerOrder = (orderId, reason, auto = false) => async (dispatch) => {
  if (!orderId) return;
  const { partnerOrderEditRef } = firebase;
  dispatch({ type: RESPOND_PARTNER_ORDER, payload: { orderId, action: 'reject' } });
  dispatch(dismissPartnerIncoming(orderId));
  await update(partnerOrderEditRef(orderId), {
    status: 'REJECTED',
    rejectionReason: reason || (auto ? 'timeout' : 'partner_rejected'),
    rejectedAt: Date.now(),
    updatedAt: Date.now(),
  });
};

/**
 * Transición genérica de status para PartnerOrders/OrderDetail (Fase 8).
 * Ej: 'IN_PREPARATION' -> 'READY' -> 'OUT_FOR_DELIVERY' -> 'COMPLETED'.
 */
export const updatePartnerOrderStatus = (orderId, status, extra = {}) => async () => {
  if (!orderId || !status) return;
  const { partnerOrderEditRef } = firebase;
  const stamps = {};
  if (status === 'CONFIRMED') stamps.acceptedAt = Date.now();
  if (status === 'READY') stamps.readyAt = Date.now();
  if (status === 'COMPLETED') stamps.completedAt = Date.now();
  if (status === 'CANCELLED') stamps.cancelledAt = Date.now();
  await update(partnerOrderEditRef(orderId), {
    status,
    ...stamps,
    ...extra,
    updatedAt: Date.now(),
  });
};
